import { ChangeEvent, FormEvent, useMemo, useState } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@context/AuthContext'
import { AuthScope, LoginCredentials, PortalKey, UserRole } from '@/types'
import { PortalHero, PortalHeroProps } from '@portals/shared/components/PortalHero'
import { DhruvantaSignInButton } from '@/components/auth/DhruvantaSignInButton'
import './PortalLoginPage.css'

interface PortalLoginPageProps {
  portal: PortalKey
  title: string
  description: string | string[]
  scope?: AuthScope
  requestedScopes?: string[]
  redirectPath?: string
  allowedRoles?: UserRole[]
  supportRegister?: boolean
  registerPath?: string
}

const sharedLogo = '/logo.png'

const portalHeroContent: Record<PortalKey, Omit<PortalHeroProps, 'portal'>> = {
  customer: {
    eyebrow: 'Unified marketplace',
    title: 'Shop smarter with Dhruvanta Retail',
    subtitle: 'Discovery-led browsing, doorstep delivery, and digital payments under one login.',
    description:
      'Switch between curated categories, family carts, and assisted shopping experiences without losing context.',
    icon: '✨',
    logoSrc: sharedLogo,
    highlights: [
      { label: 'Live categories', value: '28+' },
      { label: 'Average delivery', value: '< 48 hrs' },
      { label: 'Active members', value: '120k' }
    ],
    featureWords: [
      'Same-day delivery',
      'Cart sync',
      'UPI & cards',
      'Reward vault',
      'Family sharing',
      'Regional support'
    ]
  },
  seller: {
    eyebrow: 'Seller workspace',
    title: 'Launch, optimize, and reconcile your storefront',
    subtitle: 'Catalog sync, delivery orchestration, and revenue analytics live here.',
    description:
      'Manage listings, automate promotions, and monitor payouts for every Dhruvanta Retail surfacing channel.',
    icon: '🏪',
    logoSrc: sharedLogo,
    highlights: [
      { label: 'Catalog uptime', value: '99.9%' },
      { label: 'Avg delivery', value: '1.8 days' },
      { label: 'Team roles', value: '5+' }
    ],
    featureWords: [
      'Catalog sync',
      'Smart repricing',
      'Flash deals',
      'Returns routing',
      'Team controls',
      'Finance exports'
    ]
  },
  delivery: {
    eyebrow: 'Fleet console',
    title: 'Stay ahead of every drop',
    subtitle: 'Assignments, live location sharing, and proof capture in one console.',
    description:
      'Your manifest syncs from dispatch so you can focus on doorstep handoffs, OTP verification, and escalations.',
    icon: '🚚',
    logoSrc: sharedLogo,
    highlights: [
      { label: 'Routes per day', value: '12' },
      { label: 'SLA hit rate', value: '98.4%' },
      { label: 'Proof captures', value: '4.8 avg' }
    ],
    featureWords: [
      'Live routing',
      'OTP drop-offs',
      'Location share',
      'Cash collection',
      'Exception logging',
      'Team huddles'
    ]
  },
  operations: {
    eyebrow: 'Control tower',
    title: 'Command onboarding and service recovery',
    subtitle: 'Workflow escalations, returns governance, and partner upgrades stay in sync.',
    description:
      'Triage onboarding queues, approve delivery partner expansions, and close refunds with full telemetry.',
    icon: '🛰️',
    logoSrc: sharedLogo,
    highlights: [
      { label: 'Apps in review', value: '42' },
      { label: 'Open tasks', value: '19' },
      { label: 'Return SLAs', value: '96%' }
    ],
    featureWords: [
      'Onboarding queue',
      'Task claiming',
      'Returns audit',
      'Partner upgrades',
      'SLA tracking',
      'Ops playbooks'
    ]
  },
  admin: {
    eyebrow: 'Executive admin',
    title: 'Oversee the Dhruvanta Retail platform',
    subtitle: 'Compliance dashboards, finance exports, and RBAC policy tooling.',
    description:
      'Reconcile payouts, unblock sellers, and review audit trails across every service boundary.',
    icon: '🛡️',
    logoSrc: sharedLogo,
    highlights: [
      { label: 'Total users', value: '250k' },
      { label: 'Active sellers', value: '4.2k' },
      { label: 'Audit trails', value: '24/7' }
    ],
    featureWords: [
      'RBAC',
      'Policy engine',
      'Finance exports',
      'Audit center',
      'Workflow overrides',
      'Data rooms'
    ]
  }
}

export function PortalLoginPage({
  portal,
  title,
  description,
  scope,
  requestedScopes = [],
  redirectPath = '/',
  allowedRoles,
  supportRegister = false,
  registerPath
}: PortalLoginPageProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { authState, login } = useAuth()

  // A protected-route guard (RequireAuth) may redirect here with ?next=<attempted-path>; honor it
  // on success so the user lands back where they were headed. Only same-origin relative paths are
  // accepted to avoid an open-redirect.
  const postLoginTarget = useMemo(() => {
    const next = new URLSearchParams(location.search).get('next')
    if (next) {
      const decoded = decodeURIComponent(next)
      if (decoded.startsWith('/') && !decoded.startsWith('//')) {
        return decoded
      }
    }
    return redirectPath
  }, [location.search, redirectPath])

  const [formState, setFormState] = useState<LoginCredentials>({
    email: '',
    password: '',
    otpCode: '',
    otpChallengeId: undefined
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpChannel, setOtpChannel] = useState<string | null>(null);

  const heroCopy = useMemo(
    () => portalHeroContent[portal] ?? portalHeroContent.customer,
    [portal]
  )

  const descriptionLines = Array.isArray(description) ? description : [description];

  const resetOtpFlow = () => {
    setOtpStep(false);
    setOtpChannel(null);
    setFormState(prev => ({
      ...prev,
      otpCode: '',
      otpChallengeId: undefined
    }));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (otpStep && (name === 'email' || name === 'password')) {
      resetOtpFlow();
    }
    setFormState(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const result = await login(formState, scope, requestedScopes);
      if (result.status === 'otp_required') {
        setOtpStep(true);
        setOtpChannel(result.channel ?? null);
        setFormState(prev => ({
          ...prev,
          otpChallengeId: result.challengeId,
          otpCode: ''
        }));
        return;
      }

      navigate(postLoginTarget, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const allowedRoleList = allowedRoles ?? [];
  const roleAllowed =
    allowedRoleList.length === 0 || (authState.role ? allowedRoleList.includes(authState.role) : false);

  if (authState.isAuthenticated && roleAllowed) {
    return <Navigate to={postLoginTarget} replace />;
  }

  return (
    <div className={`portal-login portal-login-${portal}`}>
      <div className="portal-login__layout">
        <div className="portal-login__hero">
          <PortalHero portal={portal} {...heroCopy} />
        </div>
        <div className="portal-login__card">
          <header className="portal-login__header">
            <span className="portal-login__badge">{portal.toUpperCase()}</span>
            <h1>{title}</h1>
            <div className="portal-login__description">
              {descriptionLines.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
            {allowedRoles && allowedRoles.length > 0 && (
              <div className="portal-login__allowed">
                <strong>Allowed Roles:</strong>{' '}
                {allowedRoles
                  .map(role => role.replace(/_/g, ' ').toLowerCase())
                  .map((role, idx, arr) => (idx < arr.length - 1 ? `${role}, ` : role))}
              </div>
            )}
          </header>

          <form className="portal-login__form" onSubmit={handleSubmit}>
            <label className="portal-login__form-label" htmlFor="portal-login-email">
              Email
            </label>
            <input
              id="portal-login-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formState.email}
              onChange={handleChange}
              disabled={isSubmitting || otpStep}
            />

            <label className="portal-login__form-label" htmlFor="portal-login-password">
              Password
            </label>
            <input
              id="portal-login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={formState.password}
              onChange={handleChange}
              disabled={isSubmitting || otpStep}
            />

            {!otpStep && (
              <button
                type="button"
                className="portal-login__secondary portal-login__secondary--ghost portal-login__forgot"
                onClick={() => navigate('/forgot-password')}
                disabled={isSubmitting}
              >
                Forgot password?
              </button>
            )}

            {otpStep && (
              <div className="portal-login__otp-block">
                <p className="portal-login__otp-hint">
                  Enter the verification code sent to your {otpChannel ? otpChannel.toLowerCase() : 'enrolled channel'}.
                </p>
                <label className="portal-login__form-label" htmlFor="portal-login-otp">
                  One-Time Code
                </label>
                <input
                  id="portal-login-otp"
                  name="otpCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={formState.otpCode ?? ''}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>
            )}

            {error && <div className="portal-login__error">{error}</div>}

            <button type="submit" className="portal-login__submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : otpStep ? 'Verify Code' : 'Sign In'}
            </button>
            {/* KAN-2036 — the role portal is passed EXPLICITLY so the BFF broker
                seats the right per-role session (customer/seller/delivery/
                operations/admin), rather than inferring it from the DOM. */}
            {!otpStep && <DhruvantaSignInButton portal={portal} />}
          </form>

          <footer className="portal-login__footer">
            <button
              type="button"
              className="portal-login__secondary"
              onClick={() => {
                if (otpStep) {
                  resetOtpFlow();
                } else {
                  navigate(-1);
                }
              }}
              disabled={isSubmitting}
            >
              {otpStep ? 'Edit Credentials' : 'Back'}
            </button>
            {supportRegister && registerPath && (
              <button
                type="button"
                className="portal-login__secondary portal-login__secondary--ghost"
                onClick={() => navigate(registerPath)}
                disabled={isSubmitting}
              >
                Register Instead
              </button>
            )}
          </footer>
        </div>
      </div>
    </div>
  )
}

export default PortalLoginPage;
